import React, { useMemo, useState } from "react";
import { Link2, StickyNote, X, Info, BookmarkCheck, ChevronLeft, ChevronRight } from "lucide-react";

import type { NodeData, Contact, Link as LinkType, Note, StructureContact } from "../lib/api";
import { NODE_TYPES } from "../lib/nodeTypes";
import { getContactCounts, normalizeContactType } from "../lib/contacts";
import { ContactDisplay } from "./ContactDisplay";

type Props = {
  node: NodeData | null;
  onClose: () => void;
  allNodes?: NodeData[];
  onNavigate?: (nodeId: string) => void;
  contactLookup?: Record<string, StructureContact>;
};

const contactChannelType = (contact: Contact) =>
  (contact as any).contact_type || (contact as any).type || (contact as any).contactType || "";

const isBoardContact = (contact: Contact) => {
  if ((contact as any).is_board_member) return true;
  return normalizeContactType(contactChannelType(contact)) === "board_member";
};

const byPriority = (contacts: Contact[]) =>
  contacts
    .map((c) => ({
      ...c,
      contact_type: contactChannelType(c),
    }))
    .sort((a, b) => {
      const prioA = contactChannelType(a) === "primary" ? 0 : 1;
      const prioB = contactChannelType(b) === "primary" ? 0 : 1;
      return prioA - prioB;
    });

const labelForType = (type?: string | null) => {
  if (!type) return "";
  const t = NODE_TYPES.find((n) => n.id === type);
  return t ? t.label : type;
};

export const NodeOverview: React.FC<Props> = ({ node, onClose, allNodes = [], onNavigate, contactLookup = {} }) => {
  if (!node) return null;

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const contacts = useMemo(() => {
    const ids = ((node as any).contact_ids || []) as string[];
    const roles = ((node as any).contact_roles || {}) as Record<string, string>;
    const fromIds: Contact[] = ids
      .map((id) => {
        const c = contactLookup[id];
        if (!c) return null;
        return {
          ...(c as any),
          contact_type: roles[id] || c.contact_type,
        } as Contact;
      })
      .filter(Boolean) as Contact[];
    const fallback = (node.contacts || []).map((c: any) => ({
      ...c,
      contact_type: c.contact_type || c.contactType || c.type,
    }));
    const base = fromIds.length > 0 ? fromIds : fallback;
    return byPriority(base);
  }, [contactLookup, node]);

  const board = contacts.filter((c) => isBoardContact(c));
  const group = contacts.filter((c) => !isBoardContact(c));
  const { board: boardCount, group: groupCount } = getContactCounts(contacts as any);
  const links = (node.links || []) as LinkType[];
  const notes = (node.notes || []) as Note[];
  const nodeTypeLabel = labelForType(node.type);

  // Find current index and navigation
  const currentIndex = allNodes.findIndex((n) => n.id === node.id);
  const nodeCount = allNodes.length;
  const hasMultiple = nodeCount > 1;
  const isValidIndex = currentIndex >= 0;
  const prevIndex = isValidIndex ? (currentIndex - 1 + nodeCount) % nodeCount : 0;
  const nextIndex = isValidIndex ? (currentIndex + 1) % nodeCount : 0;
  const showNavigation = hasMultiple && isValidIndex && onNavigate;
  const headerTextColor = node.text_color || "var(--color-text)";

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !onNavigate || !showNavigation) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // Swipe left = next node
      onNavigate(allNodes[nextIndex].id);
    }
    if (isRightSwipe) {
      // Swipe right = previous node
      onNavigate(allNodes[prevIndex].id);
    }
  };

  const handlePrevious = () => {
    if (showNavigation && onNavigate) {
      onNavigate(allNodes[prevIndex].id);
    }
  };

  const handleNext = () => {
    if (showNavigation && onNavigate) {
      onNavigate(allNodes[nextIndex].id);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 md:px-4" onClick={onClose}>
      <div
        className="w-full h-full md:h-auto md:max-w-2xl md:max-h-[85vh] md:rounded-2xl bg-[var(--color-panel)] text-[var(--color-text)] shadow-2xl overflow-hidden flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="px-4 md:px-5 py-4 md:py-4 flex items-center gap-3 flex-shrink-0 sticky top-0 z-10"
          style={{ background: node.color || "var(--color-panel-2)", color: headerTextColor }}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide opacity-80" style={{ color: headerTextColor }}>
              <Info size={14} />
              Übersicht
              {showNavigation && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-black/10 text-[10px] font-semibold" style={{ color: headerTextColor }}>
                  {currentIndex + 1} / {allNodes.length}
                </span>
              )}
            </div>
            <div className="text-lg md:text-2xl font-black title-font leading-tight truncate" style={{ color: headerTextColor }}>
              {node.label}
            </div>
            <div className="text-xs md:text-sm opacity-80" style={{ color: headerTextColor }}>
              Typ: <span className="font-semibold">{nodeTypeLabel}</span>
            </div>
          </div>
          <button className="p-2 md:p-2 rounded-full bg-black/10 hover:bg-black/20 active:bg-black/30 min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto px-4 md:px-5 py-4 md:py-4 space-y-5 flex-1">
          {showNavigation && (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-2)]/80 px-3 py-2">
              <button
                onClick={handlePrevious}
                disabled={!showNavigation}
                className={`flex items-center gap-1 px-3 py-2 rounded-full border border-[var(--color-border)] text-sm font-semibold ${
                  showNavigation
                    ? "bg-[var(--color-panel)] hover:bg-[var(--color-panel-2)] active:scale-95 transition-transform"
                    : "bg-[var(--color-panel)] opacity-40 cursor-not-allowed"
                }`}
                aria-label="Vorherige Node"
              >
                <ChevronLeft size={18} /> Zurück
              </button>
              <button
                onClick={handleNext}
                disabled={!showNavigation}
                className={`flex items-center gap-1 px-3 py-2 rounded-full border border-[var(--color-border)] text-sm font-semibold ${
                  showNavigation
                    ? "bg-[var(--color-panel)] hover:bg-[var(--color-panel-2)] active:scale-95 transition-transform"
                    : "bg-[var(--color-panel)] opacity-40 cursor-not-allowed"
                }`}
                aria-label="Nächste Node"
              >
                Weiter <ChevronRight size={18} />
              </button>
            </div>
          )}
          <section className="space-y-2">
            <div className="text-sm font-semibold subtitle-font text-[var(--color-muted)]">Beschreibung</div>
            <div className="text-sm md:text-base leading-relaxed">
              {node.description && node.description.trim().length > 0 ? node.description : "Keine Beschreibung hinterlegt."}
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold subtitle-font text-[var(--color-muted)]">
              <BookmarkCheck size={18} /> Ansprechpartner
              <span className="text-xs text-[var(--color-muted)]">Vorstand: {boardCount} · Gruppe: {groupCount}</span>
            </div>
            {contacts.length === 0 ? (
              <div className="text-sm text-[var(--color-muted)]">Keine Ansprechpartner hinterlegt.</div>
            ) : (
              <>
                {board.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-[12px] uppercase tracking-wide text-[var(--color-muted)]">Vorstand</div>
                    <div className="grid gap-2">
                      {board.map((c) => (
                        <ContactDisplay key={(c as any).id || c.name} contact={c as any} />
                      ))}
                    </div>
                  </div>
                )}

                {group.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-[12px] uppercase tracking-wide text-[var(--color-muted)]">Gruppe</div>
                    <div className="grid gap-2">
                      {group.map((c) => (
                        <ContactDisplay key={(c as any).id || c.name} contact={c as any} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold subtitle-font text-[var(--color-muted)]">
              <Link2 size={18} /> Links
            </div>
            {links.length === 0 ? (
              <div className="text-sm text-[var(--color-muted)]">Keine Links.</div>
            ) : (
              <div className="grid gap-2">
                {links.map((l) => {
                  const title = l.title || l.url;
                  return (
                    <a
                      key={l.id || l.url}
                      href={l.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-2)] px-4 py-3 hover:border-[var(--color-accent)] transition-colors min-h-[44px]"
                    >
                      <div className="font-semibold text-sm md:text-base break-words">{title}</div>
                      {l.description && <div className="text-xs md:text-sm text-[var(--color-muted)] mt-1">{l.description}</div>}
                    </a>
                  );
                })}
              </div>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold subtitle-font text-[var(--color-muted)]">
              <StickyNote size={18} /> Notizen
            </div>
            {notes.length === 0 ? (
              <div className="text-sm text-[var(--color-muted)]">Keine Notizen.</div>
            ) : (
              <div className="grid gap-2">
                {notes.slice(0, 5).map((n, idx) => (
                  <div
                    key={(n.id as any) || idx}
                    className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-2)] px-4 py-3 text-sm md:text-base"
                  >
                    {n.text}
                  </div>
                ))}
                {notes.length > 5 && (
                  <div className="text-xs text-[var(--color-muted)]">+ {notes.length - 5} weitere Notizen.</div>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};
